import React, { useState } from 'react';
import './Steps.css';

const Step4 = ({ bookingData, updateBookingData, nextStep, prevStep }) => {
  const monthNames = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
  const timeSlots = [
    '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  const [sameHourForAll, setSameHourForAll] = useState(false);
  const [commonTime, setCommonTime] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState(bookingData.selectedDates || []);

  // Get required number of dates based on package type
  const getRequiredDates = () => {
    if (bookingData.packageType === 'single') return 1;
    if (bookingData.packageType === 'multiple') return bookingData.packageQuantity;
    if (bookingData.packageType === 'skiday') return bookingData.packageQuantity || 1;
    return 1;
  };

  const requiredDates = getRequiredDates();

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek: startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1 };
  };

  const isDateDisabled = (date) => {
    const now = new Date();
    const currentHour = now.getHours();
    
    let cutoffDate = new Date(now);
    if (currentHour >= 17) {
      cutoffDate.setDate(cutoffDate.getDate() + 1);
    }
    cutoffDate.setHours(0, 0, 0, 0);
    
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    
    return dateToCheck < cutoffDate;
  };

  const isDateSelected = (date) => {
    return selectedDates.some(d => 
      d.getDate() === date.getDate() &&
      d.getMonth() === date.getMonth() &&
      d.getFullYear() === date.getFullYear()
    );
  };

  const handleDateToggle = (date) => {
    if (isDateDisabled(date)) return;
    
    let updatedDates;
    
    if (isDateSelected(date)) {
      // Remove date
      updatedDates = selectedDates.filter(d => 
        !(d.getDate() === date.getDate() &&
          d.getMonth() === date.getMonth() &&
          d.getFullYear() === date.getFullYear())
      );
      
      // Remove time slot for this date
      const dateKey = date.toISOString().split('T')[0];
      const updatedSlots = { ...bookingData.timeSlots };
      delete updatedSlots[dateKey];
      updateBookingData('timeSlots', updatedSlots);
    } else {
      // Add date
      if (selectedDates.length < requiredDates) {
        updatedDates = [...selectedDates, date].sort((a, b) => a - b);
      } else {
        // Replace oldest date
        updatedDates = [...selectedDates.slice(1), date].sort((a, b) => a - b);
        
        const removedDate = selectedDates[0];
        const dateKey = removedDate.toISOString().split('T')[0];
        const updatedSlots = { ...bookingData.timeSlots };
        delete updatedSlots[dateKey];
        updateBookingData('timeSlots', updatedSlots);
      }
      
      // Apply common time if set
      if (sameHourForAll && commonTime) {
        const dateKey = date.toISOString().split('T')[0];
        const updatedSlots = {
          ...bookingData.timeSlots,
          [dateKey]: commonTime
        };
        updateBookingData('timeSlots', updatedSlots);
      }
    }
    
    setSelectedDates(updatedDates);
    updateBookingData('selectedDates', updatedDates);
  };

  const handleTimeSlotChange = (date, time) => {
    const dateKey = date.toISOString().split('T')[0];
    const updatedSlots = {
      ...bookingData.timeSlots,
      [dateKey]: time
    };
    updateBookingData('timeSlots', updatedSlots);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
    const days = [];
    
    // Empty cells before first day
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const disabled = isDateDisabled(date);
      const selected = isDateSelected(date);
      
      days.push(
        <button
          key={day}
          className={`calendar-day ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={() => handleDateToggle(date)}
          disabled={disabled}
        >
          {day}
        </button>
      );
    }
    
    return days;
  };

  const handleSameHourToggle = (checked) => {
    setSameHourForAll(checked);
    if (!checked) {
      setCommonTime('');
    }
  };

  const handleCommonTimeChange = (time) => {
    setCommonTime(time);
    
    if (time && sameHourForAll) {
      const updatedSlots = { ...bookingData.timeSlots };
      selectedDates.forEach(date => {
        const dateKey = date.toISOString().split('T')[0];
        updatedSlots[dateKey] = time;
      });
      updateBookingData('timeSlots', updatedSlots);
    }
  };

  const handleNext = () => {
    if (selectedDates.length === 0) return;
    
    const allDatesHaveTime = selectedDates.every(date => {
      const dateKey = date.toISOString().split('T')[0];
      return bookingData.timeSlots[dateKey];
    });
    
    if (allDatesHaveTime) {
      nextStep();
    }
  };

  const getInstructionText = () => {
    switch (bookingData.packageType) {
      case 'single':
        return 'Selectați data pentru lecție';
      case 'multiple':
        return `Selectați ${bookingData.packageQuantity} date pentru lecții`;
      case 'skiday':
        return `Selectați ${bookingData.packageQuantity || 1} ${(bookingData.packageQuantity || 1) === 1 ? 'zi' : 'zile'} pentru Ski Day`;
      default:
        return 'Selectați zilele pentru lecții';
    }
  };

  const formatDate = (date) => {
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const has48HourWarning = () => {
    const now = new Date();
    const currentHour = now.getHours();
    
    let thresholdDate = new Date(now);
    if (currentHour >= 17) {
      thresholdDate.setDate(thresholdDate.getDate() + 1);
    }
    thresholdDate.setHours(0, 0, 0, 0);
    thresholdDate.setDate(thresholdDate.getDate() + 2);
    
    return selectedDates.some(date => {
      const dateToCheck = new Date(date);
      dateToCheck.setHours(0, 0, 0, 0);
      return dateToCheck < thresholdDate;
    });
  };

  return (
    <div className="step-container">
      <h2 className="step-heading">Configurați programul</h2>
      <p className="step-description">{getInstructionText()}</p>

      {has48HourWarning() && (
        <div className="warning-banner">
          Informare: Pentru rezervările sub 48 de ore, vă vom contacta telefonic după trimiterea formularului pentru a confirma disponibilitatea instructorului.
        </div>
      )}

      <div className="schedule-container">
        <div className="calendar-section">
          <div className="calendar-header">
            <button className="calendar-nav-btn" onClick={() => navigateMonth(-1)}>←</button>
            <h3 className="calendar-month">{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
            <button className="calendar-nav-btn" onClick={() => navigateMonth(1)}>→</button>
          </div>
          
          <div className="calendar-weekdays">
            {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, i) => (
              <div key={i} className="calendar-weekday">{day}</div>
            ))}
          </div>
          
          <div className="calendar-grid">
            {renderCalendar()}
          </div>

          {/* Selection progress indicator */}
          <div className="selection-progress">
            {selectedDates.length < requiredDates ? (
              <p className="selection-reminder">
                📅 Selectați încă {requiredDates - selectedDates.length} {requiredDates - selectedDates.length === 1 ? 'zi' : 'zile'}
              </p>
            ) : (
              <p className="selection-complete">
                ✓ Ați selectat toate zilele necesare
              </p>
            )}
          </div>
          
          {selectedDates.length > 0 && (
            <div className="selected-dates-info">
              <h4>Date selectate ({selectedDates.length}):</h4>
              <div className="selected-dates-list">
                {selectedDates.map((date, idx) => (
                  <span key={idx} className="selected-date-badge">
                    {formatDate(date)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Time slot selection */}
        {selectedDates.length > 0 && (
          <div className="time-slots-selection">
            <div className="time-header">
              <h3 className="subsection-title">Alegeți orele</h3>
              
              {selectedDates.length > 1 && (
                <label className="same-hour-checkbox">
                  <input
                    type="checkbox"
                    checked={sameHourForAll}
                    onChange={(e) => handleSameHourToggle(e.target.checked)}
                  />
                  <span>Aceeași oră pentru toate zilele</span>
                </label>
              )}
            </div>

            {sameHourForAll && selectedDates.length > 1 ? (
              <div className="common-time-selector">
                <label className="common-time-label">Selectați ora pentru toate zilele:</label>
                <select
                  value={commonTime}
                  onChange={(e) => handleCommonTimeChange(e.target.value)}
                  className="time-select-large"
                >
                  <option value="">Selectați ora</option>
                  {timeSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
                <p className="time-note">
                  ✓ Ora va fi aplicată automat pentru toate zilele selectate
                </p>
              </div>
            ) : (
              <div className="individual-times">
                {selectedDates.map(date => {
                  const dateKey = date.toISOString().split('T')[0];
                  return (
                    <div key={dateKey} className="day-time-row">
                      <label className="day-label">{formatDate(date)}:</label>
                      <select
                        value={bookingData.timeSlots[dateKey] || ''}
                        onChange={(e) => handleTimeSlotChange(date, e.target.value)}
                        className="time-select"
                      >
                        <option value="">Selectați ora</option>
                        {timeSlots.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="step-actions">
        <button className="btn-secondary" onClick={prevStep}>
          ← Înapoi
        </button>
        <button 
          className="btn-primary"
          onClick={handleNext}
          disabled={
            selectedDates.length !== requiredDates || 
            !selectedDates.every(date => bookingData.timeSlots[date.toISOString().split('T')[0]])
          }
        >
          Continuă →
        </button>
      </div>
    </div>
  );
};

export default Step4;